import React from 'react';
import { formatPrice, formatDateOnly } from '../utils';
import { getSummaryContingencyKeys } from '../contingencyCatalog';
import './CompetingOffersTable.css';

const contingencyCount = (offer) => getSummaryContingencyKeys(offer?.contingencies).length;

/**
 * Best value in a column, but only when the offers actually differ on it —
 * marking every cell when they're all equal is noise, not a signal.
 */
const extreme = (values, pick) => {
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  if (lo === hi) return null; // no variation → nothing to highlight
  return pick === 'max' ? hi : lo;
};

/**
 * CompetingOffersTable — the live contenders on one listing lined up so the
 * seller can weigh the terms price alone hides: a lower bid with 30% down, a big
 * earnest deposit and no contingencies can beat a higher one. The strongest
 * value in each comparable column is flagged (highest down payment and earnest,
 * fewest contingencies, highest price); closing date is shown without a verdict
 * since "better" depends on the seller. Rows are ordered by price, high to low.
 *
 * Fed the true contenders (not the filtered view), so the comparison is complete
 * even when a status filter hides a card below.
 */
export default function CompetingOffersTable({ offers = [] }) {
  if (offers.length < 2) return null;

  const rows = [...offers].sort(
    (a, b) => (Number(b.offer_price) || 0) - (Number(a.offer_price) || 0),
  );

  const bestPrice = extreme(rows.map((o) => Number(o.offer_price) || 0), 'max');
  const bestDown = extreme(rows.map((o) => Number(o.down_payment_percent) || 0), 'max');
  const bestEarnest = extreme(rows.map((o) => Number(o.earnest_money) || 0), 'max');
  const bestContingencies = extreme(rows.map(contingencyCount), 'min');

  const cell = (isBest, children, testid) => (
    <td className={isBest ? 'competing-table__cell competing-table__cell--best' : 'competing-table__cell'} data-testid={testid}>
      {children}
      {isBest ? <span className="competing-table__sr"> (strongest)</span> : null}
    </td>
  );

  return (
    <div className="competing-table-wrap" role="region" aria-label="Compare the live offers" tabIndex={0}>
      <table className="competing-table" data-testid="competing-table">
        <thead>
          <tr>
            <th scope="col">Offer</th>
            <th scope="col">Down payment</th>
            <th scope="col">Earnest</th>
            <th scope="col">Closing</th>
            <th scope="col">Contingencies</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((o) => {
            const price = Number(o.offer_price) || 0;
            const down = Number(o.down_payment_percent) || 0;
            const earnest = o.earnest_money != null ? Number(o.earnest_money) : null;
            const conts = contingencyCount(o);
            return (
              <tr key={o.id} data-testid={`compare-row-${o.id}`}>
                <th scope="row" className="competing-table__offer">
                  {formatPrice(o.offer_price)}
                  {bestPrice != null && price === bestPrice ? (
                    <span className="competing-table__lead" data-testid={`compare-lead-${o.id}`}>★ highest</span>
                  ) : null}
                </th>
                {cell(bestDown != null && down === bestDown, o.down_payment_percent != null ? `${o.down_payment_percent}%` : '—', `compare-down-${o.id}`)}
                {cell(
                  bestEarnest != null && earnest === bestEarnest,
                  earnest != null ? formatPrice(earnest) : '—',
                  `compare-earnest-${o.id}`,
                )}
                <td className="competing-table__cell">{formatDateOnly(o.closing_date)}</td>
                {cell(
                  bestContingencies != null && conts === bestContingencies,
                  conts === 0 ? 'None' : conts,
                  `compare-cont-${o.id}`,
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
